import { useEffect, useState } from "react";
import { C } from "../lib/theme";
import { Icon } from "./Icon";
import { KapioLogo } from "./KapioLogo";
import { useCart } from "../store/cartStore";
import { supabase } from "../lib/supabase";

// Nav definitions. Badges are live counts, not hardcoded — each consumer
// resolves them from the store (cart) or a Supabase query (orders, messages).
const CUST_NAV = [
  { id: "discover", label: "Discover", icon: "compass" },
  { id: "stores", label: "Stores", icon: "store" },
  { id: "cart", label: "Cart", icon: "shopping-cart", badgeKey: "cart" },
  { id: "orders", label: "Orders", icon: "package" },
  { id: "messages", label: "Messages", icon: "message-square", badgeKey: "unread" },
];

const SELL_NAV = [
  { id: "dashboard", label: "Dashboard", icon: "layout-dashboard" },
  { id: "sorders", label: "Orders", icon: "package", badgeKey: "sorders_new" },
  { id: "products", label: "Products", icon: "tag" },
  { id: "smessages", label: "Messages", icon: "message-square", badgeKey: "sunread" },
  { id: "analytics", label: "Analytics", icon: "bar-chart-3" },
];

export function TopNav({ role, setRole, active, setActive, user, seller, onOpenSearch }) {
  const cartCount = useCart((s) => s.count());
  const [unread, setUnread] = useState(0);
  const [pendingOrders, setPendingOrders] = useState(0);
  const [sellerUnread, setSellerUnread] = useState(0);
  const [newSellerOrders, setNewSellerOrders] = useState(0);

  const isSeller = role === "seller";
  const nav = isSeller ? SELL_NAV : CUST_NAV;
  const badges = {
    cart: cartCount,
    unread,
    sorders_new: newSellerOrders,
    sunread: sellerUnread,
    orders: pendingOrders,
  };
  const displayName = isSeller
    ? seller?.store_name || user?.name || "Store"
    : user?.name?.split(" ")[0] || "Guest";
  const avatarUrl = isSeller ? seller?.logo_url : user?.avatar_url;
  const initial = (displayName[0] || "K").toUpperCase();

  // Live badge counts — all from Supabase, zero hardcoded.
  useEffect(() => {
    if (!user?.id) {
      setUnread(0);
      setPendingOrders(0);
      setSellerUnread(0);
      setNewSellerOrders(0);
      return;
    }
    let cancelled = false;
    async function loadBadges() {
      // Unread counts live on the conversations row (buyer_unread_count / seller_unread_count).
      // The messages table has no recipient_id — reads are tracked per-conversation.
      const [{ data: buyerConvs }, { count: ordersCount }] = await Promise.all([
        supabase.from("conversations").select("buyer_unread_count").eq("buyer_id", user.id),
        supabase
          .from("orders")
          .select("id", { count: "exact", head: true })
          .eq("buyer_id", user.id)
          .in("status", ["pending_payment", "confirmed", "preparing", "shipped"]),
      ]);
      if (cancelled) return;
      const buyerUnread = (buyerConvs || []).reduce((s, c) => s + (c.buyer_unread_count || 0), 0);
      setUnread(buyerUnread);
      setPendingOrders(ordersCount || 0);

      if (seller?.id) {
        const [{ data: sellerConvs }, { count: sNew }] = await Promise.all([
          supabase.from("conversations").select("seller_unread_count").eq("seller_id", seller.id),
          supabase
            .from("sub_orders")
            .select("id", { count: "exact", head: true })
            .eq("seller_id", seller.id)
            .eq("status", "confirmed"),
        ]);
        if (cancelled) return;
        const sellerUnreadSum = (sellerConvs || []).reduce((s, c) => s + (c.seller_unread_count || 0), 0);
        setSellerUnread(sellerUnreadSum);
        setNewSellerOrders(sNew || 0);
      }
    }
    loadBadges();
    const interval = setInterval(loadBadges, 30_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [user?.id, seller?.id]);

  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 60,
        background: "rgba(250,250,247,0.85)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        borderBottom: "1px solid rgba(196,193,183,0.3)",
      }}
    >
      <div style={{ maxWidth: 1440, margin: "0 auto", padding: "10px 28px", display: "flex", alignItems: "center", gap: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <KapioLogo size={30} />
          <span style={{ fontSize: 17, fontWeight: 900, color: C.hunter, letterSpacing: "-0.4px" }}>Kapio</span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              padding: "2px 7px",
              borderRadius: 6,
              background: isSeller ? C.hunter : C.cream,
              color: isSeller ? C.dust : C.muted,
              letterSpacing: 1,
            }}
          >
            {isSeller ? "SELLER" : "SHOP"}
          </span>
        </div>
        <nav style={{ display: "flex", gap: 2, marginLeft: 12, flex: 1, minWidth: 0 }}>
          {nav.map((n) => {
            const on = active === n.id;
            const badge = n.badgeKey ? badges[n.badgeKey] : 0;
            return (
              <button
                key={n.id}
                onClick={() => setActive(n.id)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 7,
                  padding: "8px 14px",
                  border: "none",
                  borderRadius: 10,
                  background: on ? C.hunter : "transparent",
                  color: on ? C.dust : C.slate,
                  fontSize: 13,
                  fontWeight: on ? 700 : 600,
                  cursor: "pointer",
                }}
              >
                <Icon name={n.icon} size={15} color={on ? C.dust : C.slate} strokeWidth={on ? 2.2 : 1.5} />
                {n.label}
                {badge > 0 && (
                  <span
                    style={{
                      background: on ? C.dust : C.hunter,
                      color: on ? C.hunter : C.dust,
                      fontSize: 9.5,
                      fontWeight: 800,
                      padding: "1px 6px",
                      borderRadius: 10,
                    }}
                  >
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {seller && (
          <div
            style={{
              display: "inline-flex",
              padding: 3,
              borderRadius: 10,
              background: C.cream,
              border: "1px solid rgba(196,193,183,0.3)",
              gap: 2,
            }}
          >
            <button
              onClick={() => setRole("customer")}
              style={{
                border: "none",
                borderRadius: 8,
                padding: "5px 10px",
                fontSize: 11.5,
                fontWeight: 800,
                cursor: "pointer",
                background: !isSeller ? C.white : "transparent",
                color: !isSeller ? C.hunter : C.muted,
                boxShadow: !isSeller ? "0 1px 3px rgba(0,0,0,0.06)" : "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <Icon name="shopping-bag" size={12} color={!isSeller ? C.hunter : C.muted} />
              Shop
            </button>
            <button
              onClick={() => setRole("seller")}
              style={{
                border: "none",
                borderRadius: 8,
                padding: "5px 10px",
                fontSize: 11.5,
                fontWeight: 800,
                cursor: "pointer",
                background: isSeller ? C.hunter : "transparent",
                color: isSeller ? C.dust : C.muted,
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <Icon name="store" size={12} color={isSeller ? C.dust : C.muted} />
              Sell
            </button>
          </div>
        )}

        <button
          onClick={onOpenSearch}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "7px 12px",
            borderRadius: 10,
            background: C.cream,
            border: "1px solid rgba(196,193,183,0.3)",
            cursor: "pointer",
            color: C.muted,
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          <Icon name="search" size={14} color={C.muted} />
          Search<span className="kbd">⌘K</span>
        </button>

        <button
          onClick={() => setActive("profile")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "4px 10px 4px 4px",
            borderRadius: 10,
            background: active === "profile" ? C.cream : C.white,
            border: "1px solid rgba(196,193,183,0.3)",
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: avatarUrl ? `url(${avatarUrl}) center/cover` : C.hunter,
              color: C.dust,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            {!avatarUrl && initial}
          </span>
          <span style={{ fontSize: 12, fontWeight: 700 }}>{displayName}</span>
        </button>
      </div>
    </header>
  );
}
